import React from 'react';
import { NavLink } from 'react-router-dom';
import { usePermissionCheck } from '../../hooks/permissionHooks/usePermissionCheck';
import { useSelector } from 'react-redux';
import { hasPermission } from '../../config/permissions';

const PermissionAwareNavItem = ({ 
  link, 
  isCollapsed = false, 
  isMobile = false, 
  onClose, 
  children,
  className = '',
  comingSoonLinks = [],
  onLogout 
}) => {
  const { user } = useSelector(state => state.user);
  const { canView } = usePermissionCheck();
  
  // Check if this link should be visible based on permissions
  const checkLinkPermission = (linkItem) => {
    // Debug logging for Roles & Permissions link
    if (linkItem.name === "Roles & Permissions") {
      console.log('Roles & Permissions Link Debug:', {
        linkName: linkItem.name,
        user: user,
        isAuthenticated: !!user,
        isMainVendor: user?.is_main_vendor,
        userPermissions: user?.permissions,
        permissions: linkItem.permissions,
        module: linkItem.permissions?.module,
        action: linkItem.permissions?.action,
        fallbackForMainVendor: linkItem.permissions?.fallbackForMainVendor,
        restrictToMainVendor: linkItem.permissions?.restrictToMainVendor
      });
    }

    // If no user is authenticated, only show always visible items
    if (!user) {
      return linkItem.permissions?.alwaysVisible || false;
    }

    if (!linkItem.permissions) return true;
    
    const { 
      module, 
      action, 
      alwaysVisible, 
      restrictToMainVendor, 
      fallbackForMainVendor 
    } = linkItem.permissions;
    
    // Always visible items (like Profile, Help, Logout)
    if (alwaysVisible) return true;
    
    // Items restricted to main vendor only
    if (restrictToMainVendor && !user?.is_main_vendor) {
      if (linkItem.name === "Roles & Permissions") {
        console.log('Roles & Permissions: Hidden due to restrictToMainVendor flag');
      }
      return false;
    }
    
    // If no specific permissions defined, allow access
    if (!module || !action) return true;
    
    // Check user permissions
    const userPermissions = user?.permissions || [];
    const isMainVendor = user?.is_main_vendor || false;
    
    // Main vendor fallback - if enabled, main vendors get access even without explicit permissions
    if (fallbackForMainVendor && isMainVendor) {
      if (linkItem.name === "Roles & Permissions") {
        console.log('Roles & Permissions: Access granted via main vendor fallback');
      }
      return true;
    }
    
    // Handle simple array format for permissions
    if (Array.isArray(userPermissions)) {
      const hasAccess = userPermissions.includes(action);
      if (linkItem.name === "Roles & Permissions") {
        console.log('Roles & Permissions: Array permissions check:', {
          userPermissions,
          action,
          hasAccess
        });
      }
      return hasAccess;
    }
    
    // Check specific permission using the hasPermission function
    const hasAccess = hasPermission(userPermissions, isMainVendor, module, action);
    
    if (linkItem.name === "Roles & Permissions") {
      console.log('Roles & Permissions: Object permissions check:', {
        userPermissions,
        isMainVendor,
        module,
        action,
        hasAccess
      });
    }
    
    return hasAccess;
  };

  // Don't render if user doesn't have permission
  if (!checkLinkPermission(link)) {
    return null;
  }

  const isComingSoon = comingSoonLinks.includes(link.name);
  const isLogout = link.name === "Logout";

  // Handle coming soon features
  if (isComingSoon) {
    return (
      <li>
        <div 
          className={`${
            isMobile 
              ? "flex relative gap-2 justify-between items-center p-3 mb-2 w-full font-medium text-gray-400 bg-gray-50 rounded-md transition duration-300 cursor-not-allowed group text-[12px]"
              : isCollapsed
                ? "flex justify-center items-center p-3 w-full font-medium text-gray-400 bg-gray-100 rounded-md transition cursor-not-allowed group"
                : "flex gap-2 items-center p-3 mb-2 w-full font-medium text-gray-400 bg-gray-100 rounded-md transition cursor-not-allowed group text-[12px]"
          } ${className}`}
          title={isCollapsed ? `${link.name} - Coming Soon` : ''}
        >
          {isMobile ? (
            <>
              <div className="flex gap-2 items-center min-w-0">
                {link.icon}
                <p className="text-[14px] font-medium truncate">
                  {link.name}
                </p>
              </div>
              <span className="flex-shrink-0 px-2 py-1 text-[10px] font-semibold text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-full">
                Coming Soon
              </span>
            </>
          ) : isCollapsed ? (
            <div className="flex-shrink-0">{link.icon}</div>
          ) : (
            <>
              {link.icon}
              <p className="text-[14px] font-medium transition duration-300">
                {link.name.slice(0, 10)}
                <span className="ml-2 text-[10px] font-semibold bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                  Coming Soon
                </span>
              </p>
            </>
          )}
        </div>
      </li>
    );
  }

  // Handle logout button
  if (isLogout) {
    return (
      <li>
        <button
          onClick={onLogout}
          className={`${
            isMobile 
              ? "group flex items-center w-full mb-2 gap-2 rounded-md p-3 font-semibold text-[12px] transition duration-300 ease-in-out cursor-pointer text-gray-700 hover:bg-[#FF6C6C] hover:text-white"
              : isCollapsed
                ? "group flex items-center justify-center w-full rounded-md p-3 font-medium transition duration-300 ease-in-out cursor-pointer text-gray-700 hover:bg-[#FF6C6C] hover:text-white"
                : "group flex items-center mb-2 w-full gap-2 rounded-md p-3 font-medium text-[12px] transition duration-300 ease-in-out cursor-pointer text-gray-700 hover:bg-[#FF6C6C] hover:text-white"
          } ${className}`}
          title={isCollapsed ? "Logout" : ''}
        >
          {isMobile ? (
            <>
              {link.icon}
              <p className="text-[14px] font-semibold transition duration-300">
                {link.name}
              </p>
            </>
          ) : isCollapsed ? (
            <div className="flex-shrink-0">{link.icon}</div>
          ) : (
            <>
              {link.icon}
              <p className="text-[14px] font-medium transition duration-300">
                {link.name}
              </p>
            </>
          )}
        </button>
      </li>
    );
  }

  // Handle regular navigation links
  return (
    <li>
      <NavLink
        to={link.link}
        className={({ isActive }) =>
          `${
            isMobile 
              ? `group flex items-center w-full mb-2 gap-2 rounded-md p-3 font-semibold text-[12px] transition duration-300 ease-in-out cursor-pointer ${
                  isActive ? "bg-[#FF6C6C] text-white" : "text-gray-700 hover:bg-[#FF6C6C] hover:text-white"
                }`
              : isCollapsed
                ? `group flex items-center justify-center w-full rounded-md p-3 font-medium transition duration-300 ease-in-out cursor-pointer ${
                    isActive ? "bg-[#FF6C6C] text-white" : "text-gray-700 hover:bg-[#FF6C6C] hover:text-white"
                  }`
                : `group flex items-center mb-2 w-full gap-2 rounded-md p-3 font-medium text-[12px] transition duration-300 ease-in-out cursor-pointer ${
                    isActive ? "bg-[#FF6C6C] text-white" : "text-gray-700 hover:bg-[#FF6C6C] hover:text-white"
                  }`
          } ${className}`
        }
        title={isCollapsed ? link.name : ''}
        onClick={isMobile ? onClose : undefined}
      >
        {isMobile ? (
          <>
            {link.icon}
            <p className="text-[14px] font-semibold transition duration-300">
              {link.name}
            </p>
          </>
        ) : isCollapsed ? (
          <div className="flex-shrink-0">{link.icon}</div>
        ) : (
          <>
            {link.icon}
            <p className="text-[14px] font-medium transition duration-300">
              {link.name}
            </p>
          </>
        )}
      </NavLink>
    </li>
  );
};

export default PermissionAwareNavItem; 